import type { ReactNode } from 'react';
import styles from './ArcadePage.module.css';

/**
 * Per-game animated motif drawn in the cabinet's upper two thirds —
 * set dressing behind the title, not a demo reel. One component,
 * nine variants keyed off the game namespace; everything is inline
 * SVG stroked/filled with the card's --accent custom property, so
 * there are no image files to keep in sync with themeGames.
 *
 * All motion is slow CSS keyframes (4-15s cycles) living in
 * ArcadePage.module.css. Every animated element's BASE styles are a
 * composed mid-cycle pose, so the reduced-motion block (animation:
 * none) renders a deliberate still frame rather than a blank start
 * state.
 */

const DRIFT_PATH = 'M -12 118 C 48 108 44 46 108 40 C 156 36 168 70 212 52';
const AURORA_PATH = 'M -10 70 C 30 40 60 96 100 66 C 140 40 170 92 210 60';
const RALLY_PATH = 'M 25 102 Q 100 16 175 102';

/* Hex ring around a centre orb (r=27 at 60-degree steps). */
const HEX_RING: Array<[number, number]> = [
  [127, 58], [113.5, 81.4], [86.5, 81.4], [73, 58], [86.5, 34.6], [113.5, 34.6],
];

/* Tetromino silhouettes as 10px cell offsets. */
const TETROMINOES: Array<{ cells: Array<[number, number]>; cls: 'A' | 'B' | 'C'; x: number; y: number }> = [
  { cells: [[0, 0], [0, 10], [0, 20], [10, 20]], cls: 'A', x: 34, y: 18 },  // L
  { cells: [[0, 0], [10, 0], [20, 0], [10, 10]], cls: 'B', x: 128, y: 44 }, // T
  { cells: [[0, 0], [10, 0], [0, 10], [10, 10]], cls: 'C', x: 84, y: 8 },   // O
];

function variantFor(ns: string, compact: boolean): ReactNode {
  switch (ns) {
    case 'evi-neon-drift':
      // A curving neon track with a light streak travelling it.
      return (
        <>
          <path className={styles.mTrack} d={DRIFT_PATH} pathLength={300} />
          <path className={styles.mStreak} d={DRIFT_PATH} pathLength={300} />
        </>
      );

    case 'evi-blackout-run':
      // Rooftops with a moving charge trail and a beacon above the city.
      return (
        <>
          <path className={styles.mTrack} d="M 10 106 V 76 H 48 V 94 H 68 V 54 H 106 V 82 H 130 V 38 H 166 V 66 H 190 V 106" />
          <path className={styles.mStreak} d="M 10 70 L 48 70 Q 62 40 80 48 L 106 48 Q 124 16 148 32 L 166 32" pathLength={300} />
          <circle className={styles.mOrbPop} cx={166} cy={22} r={7} />
        </>
      );

    case 'evi-surge-pack':
      // A flying pack with a charge trail through the lab.
      return (
        <>
          <path className={styles.mTrack} d="M 12 106 C 52 106 62 35 110 54 S 156 96 188 28" />
          <path className={styles.mStreak} d="M 12 106 C 52 106 62 35 110 54 S 156 96 188 28" pathLength={300} />
          <rect className={styles.mCell} x={102} y={42} width={15} height={22} rx={5} />
          <circle className={styles.mOrb} cx={111} cy={34} r={7} />
        </>
      );

    case 'evi-charge-chain':
      // A hex cluster of glowing orbs, one popping and fading on a loop.
      return (
        <>
          <circle className={styles.mOrb} cx={100} cy={58} r={10} />
          {HEX_RING.map(([x, y], i) => (
            <circle
              key={i}
              className={i === 1 ? styles.mOrbPop : styles.mOrb}
              cx={x}
              cy={y}
              r={8.5}
            />
          ))}
        </>
      );

    case 'evi-steady-climbing':
      // An isometric tower building up slab by slab, then resetting.
      return (
        <>
          {[
            { y: 100, cls: styles.mSlabA },
            { y: 84, cls: styles.mSlabB },
            { y: 68, cls: styles.mSlabC },
            { y: 52, cls: styles.mSlabD },
          ].map(({ y, cls }) => (
            <path
              key={y}
              className={`${styles.mSlab} ${cls}`}
              d={`M 72 ${y} L 100 ${y - 12} L 128 ${y} L 100 ${y + 12} Z`}
            />
          ))}
        </>
      );

    case 'evi-overflow': {
      // A grid of cells with droplets falling from the broken ones.
      // Compact (half-width tiles): a 12-cell grid turns to noise
      // behind text at ~160px, so render a 2x2 corner of it — one
      // broken cell, one droplet — instead of shrinking the full one.
      if (compact) {
        return (
          <>
            <rect className={styles.mCell} x={52} y={22} width={30} height={22} rx={5} />
            <rect className={styles.mCellBroken} x={100} y={22} width={30} height={22} rx={5} />
            <rect className={styles.mCell} x={52} y={58} width={30} height={22} rx={5} />
            <rect className={styles.mCell} x={100} y={58} width={30} height={22} rx={5} />
            <circle className={`${styles.mDrip} ${styles.mDripA}`} cx={115} cy={48} r={3.5} />
          </>
        );
      }
      const broken = new Set(['1,1', '3,0']);
      return (
        <>
          {Array.from({ length: 12 }, (_, k) => {
            const i = k % 4;
            const j = Math.floor(k / 4);
            const isBroken = broken.has(`${i},${j}`);
            return (
              <rect
                key={k}
                className={isBroken ? styles.mCellBroken : styles.mCell}
                x={28 + i * 38}
                y={16 + j * 30}
                width={24}
                height={18}
                rx={5}
              />
            );
          })}
          <circle className={`${styles.mDrip} ${styles.mDripA}`} cx={78} cy={66} r={3} />
          <circle className={`${styles.mDrip} ${styles.mDripB}`} cx={154} cy={36} r={3} />
        </>
      );
    }

    case 'evi-full-charge':
      // Tetromino silhouettes drifting down, a row flashing as it clears.
      // Compact: three drifting pieces overlap into clutter at
      // half-width — one T piece plus the clearing row keeps the read.
      if (compact) {
        return (
          <>
            <g className={`${styles.mPiece} ${styles.mPieceC}`} transform="translate(84 8)">
              <rect x={0} y={0} width={11} height={11} rx={2} />
              <rect x={11} y={0} width={11} height={11} rx={2} />
              <rect x={22} y={0} width={11} height={11} rx={2} />
              <rect x={11} y={11} width={11} height={11} rx={2} />
            </g>
            <rect className={styles.mClearRow} x={30} y={104} width={140} height={11} rx={3} />
          </>
        );
      }
      return (
        <>
          {TETROMINOES.map(({ cells, cls, x, y }) => (
            <g
              key={cls}
              className={`${styles.mPiece} ${styles[`mPiece${cls}`]}`}
              transform={`translate(${x} ${y})`}
            >
              {cells.map(([cx, cy], i) => (
                <rect key={i} x={cx} y={cy} width={9} height={9} rx={1.5} />
              ))}
            </g>
          ))}
          <rect className={styles.mClearRow} x={22} y={112} width={156} height={10} rx={3} />
        </>
      );

    case 'evi-current':
      // An aurora ribbon with a spark threading through it.
      return (
        <>
          <path className={styles.mRibbonWide} d={AURORA_PATH} />
          <path className={styles.mRibbon} d={AURORA_PATH} />
          <path className={styles.mSpark} d={AURORA_PATH} pathLength={300} />
        </>
      );

    case 'evi-volley':
      // A court with a ball tracing a rally arc, trail behind it.
      return (
        <>
          <line className={styles.mCourt} x1={16} y1={108} x2={184} y2={108} />
          <line className={styles.mNet} x1={100} y1={88} x2={100} y2={108} />
          <path className={styles.mArc} d={RALLY_PATH} />
          <path className={`${styles.mBallTrail}`} d={RALLY_PATH} pathLength={100} />
          <path className={`${styles.mBall}`} d={RALLY_PATH} pathLength={100} />
        </>
      );

    default:
      return null;
  }
}

export function CabinetMotif({ ns, compact = false }: { ns: string; compact?: boolean }) {
  return (
    <div className={styles.motifWrap} aria-hidden>
      <svg className={styles.motif} viewBox="0 0 200 130" preserveAspectRatio="xMidYMid slice">
        {variantFor(ns, compact)}
      </svg>
    </div>
  );
}
